import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RPC_CHAINS } from '../lib/chains';
import { useConnection } from '../lib/connection';
import { useConsensusStatus } from '../lib/consensus-status';
import { SHOW_SUBGROVES } from '../lib/flags';
import { verifyResponseProof, type VerifyResult } from '../lib/proof-verify';
import { useTheme, type Theme } from '../lib/theme';


interface Subgrove {
  subgrove_id?: string;
  id?: string;
  description?: string | null;
  mode?: string;
  chain?: string | null;
  indexer_count?: number;
  template_config?: { chain?: string | null } | null;
}

interface Kpis {
  subgroveCount: number | null;
  validatorCount: number | null;
  indexerCount: number | null;
  subgroves: Subgrove[];
}

const REFRESH_MS = 15_000;

const CHAIN_NAMES = (() => {
  const names = RPC_CHAINS.map((c) => c.name);
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
})();

// Determine which chain a subgrove indexes. Prefers the top-level `chain`
// field the listing now returns (mainnet/bsc/optimism/solana-mainnet/…),
// falling back to the legacy `template_config.chain`. Non-indexing
// subgroves (DataStorage / FileStorage) have no source chain — they live
// on Willow itself, so they bucket as "Willow".
function chainOf(s: Subgrove): string {
  const raw = (s.chain ?? s.template_config?.chain ?? '').toLowerCase().trim();
  if (raw === 'ethereum' || raw === 'mainnet' || raw === 'eth') return 'Ethereum';
  if (raw === 'bnb' || raw === 'bsc' || raw.includes('bnb')) return 'BNB Chain';
  if (raw === 'optimism') return 'Optimism';
  if (raw === 'arbitrum' || raw === 'arbitrum-one') return 'Arbitrum';
  if (raw === 'base') return 'Base';
  if (raw === 'polygon' || raw === 'matic') return 'Polygon';
  if (raw === 'solana' || raw === 'solana-mainnet') return 'Solana';
  return 'Willow';
}

// Palette ported from yieldnest dashboard's chart palette so the explorer
// donut feels cohesive with the wider Willow design system. The green-family
// chains are theme-aware: LIGHT uses the crisp emerald accent family (so the
// chart matches the refreshed light theme); DARK keeps its original muted
// greens. Non-green series (BNB/Polygon/Solana) are identical across themes.
const CHAIN_COLORS_DARK: Record<string, string> = {
  Ethereum: '#4ea882',
  'BNB Chain': '#b45309',
  Optimism: '#2c7a5c',
  Arbitrum: '#7acba7',
  Base: '#1f5e44',
  Polygon: '#8b5cf6',
  Solana: '#14b8a6',
  Willow: '#5a6b60',
};

const CHAIN_COLORS_LIGHT: Record<string, string> = {
  Ethereum: '#0a7d59',
  'BNB Chain': '#b45309',
  Optimism: '#086648',
  Arbitrum: '#34a578',
  Base: '#075c43',
  Polygon: '#8b5cf6',
  Solana: '#14b8a6',
  Willow: '#5a6473',
};

const CHAIN_COLORS_FALLBACK_DARK = '#5a6b60';
const CHAIN_COLORS_FALLBACK_LIGHT = '#5a6473';

function chainColors(theme: Theme): {
  map: Record<string, string>;
  fallback: string;
} {
  return theme === 'dark'
    ? { map: CHAIN_COLORS_DARK, fallback: CHAIN_COLORS_FALLBACK_DARK }
    : { map: CHAIN_COLORS_LIGHT, fallback: CHAIN_COLORS_FALLBACK_LIGHT };
}

async function fetchKpis(apiUrl: string): Promise<Kpis> {
  const [subRes, valsRes, idxRes] = await Promise.allSettled([
    SHOW_SUBGROVES ? fetch(`${apiUrl}/subgroves`).then((r) => r.json()) : Promise.resolve([]),
    fetch(`${apiUrl}/validators`).then((r) => r.json()),
    fetch(`${apiUrl}/indexers`).then((r) => r.json()),
  ]);

  let subgroveCount: number | null = null;
  let subgroves: Subgrove[] = [];
  if (subRes.status === 'fulfilled') {
    const arr: Subgrove[] = subRes.value?.data ?? subRes.value ?? [];
    if (Array.isArray(arr)) {
      subgroveCount = arr.length;
      subgroves = arr;
    }
  }

  let validatorCount: number | null = null;
  if (valsRes.status === 'fulfilled') {
    const arr = valsRes.value?.data ?? valsRes.value ?? [];
    if (Array.isArray(arr)) validatorCount = arr.length;
  }

  let indexerCount: number | null = null;
  if (idxRes.status === 'fulfilled') {
    const arr = idxRes.value?.data ?? idxRes.value ?? [];
    if (Array.isArray(arr)) indexerCount = arr.length;
  }

  return { subgroveCount, validatorCount, indexerCount, subgroves };
}

interface PricePoint {
  block: number;
  price: number;
}

// Convert Uniswap V3 sqrtPriceX96 to ETH price in USDC.
//   sqrtPriceX96 = sqrt(token1/token0) * 2^96
//   For pool 0x88e6...5640 (USDC/ETH 0.05%): token0 = USDC (6dp), token1 = WETH (18dp)
//   ETH_in_USDC = 2^192 / sqrtPriceX96^2 * 10^(18-6)
function priceFromSqrtPriceX96(sqrtPriceX96Str: string): number {
  const sqrtP = BigInt(sqrtPriceX96Str);
  if (sqrtP === 0n) return 0;
  const Q192 = 1n << 192n;
  // Multiply by 10^14 (10^12 for decimal scaling + extra 100 for cent precision)
  // before dividing so the BigInt result preserves cents; then divide by 100.
  const SCALE = 100_000_000_000_000n;
  const priceCents = (Q192 * SCALE) / (sqrtP * sqrtP);
  return Number(priceCents) / 100;
}

interface EthPriceResult {
  points: PricePoint[];
  verification: VerifyResult;
}

async function fetchEthPrice(indexerUrl: string): Promise<EthPriceResult> {
  const res = await fetch(`${indexerUrl}/graphql/uniswap-v3-eth-usdc`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      query: '{ swaps(first: 250, orderBy: blockNumber, orderDirection: desc) { sqrtPriceX96 blockNumber } }',
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = await res.json();
  const swaps: { sqrtPriceX96: string; blockNumber: string }[] = body?.data?.swaps ?? [];
  const points = swaps
    .map((s) => ({
      block: Number(s.blockNumber),
      price: priceFromSqrtPriceX96(s.sqrtPriceX96),
    }))
    .filter((p) => Number.isFinite(p.block) && p.price > 0)
    .sort((a, b) => a.block - b.block);
  const verification = await verifyResponseProof(body?.proof);
  return { points, verification };
}

export function HomePanel() {
  const { apiUrl, indexerUrl } = useConnection();
  const { latestBlock, hasLoaded: consensusHasLoaded } = useConsensusStatus();
  const navigate = useNavigate();
  const [theme] = useTheme();
  const { map: chainColorMap, fallback: chainColorFallback } = chainColors(theme);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pricePoints, setPricePoints] = useState<PricePoint[] | null>(null);
  const [priceVerification, setPriceVerification] = useState<VerifyResult | null>(null);

  useEffect(() => {
    // The price chart reads a subgrove.
    if (!SHOW_SUBGROVES) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { points, verification } = await fetchEthPrice(indexerUrl);
        if (!cancelled) {
          setPricePoints(points);
          setPriceVerification(verification);
        }
      } catch {
        if (!cancelled) {
          setPricePoints([]);
          setPriceVerification(null);
        }
      }
    };
    tick();
    const interval = setInterval(() => {
      if (!cancelled && !document.hidden) tick();
    }, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [indexerUrl]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchKpis(apiUrl);
        if (!cancelled) {
          setKpis(next);
          setHasLoaded(true);
        }
      } catch {
        if (!cancelled) setHasLoaded(true);
      }
    };
    tick();
    const interval = setInterval(() => {
      if (!cancelled && !document.hidden) tick();
    }, REFRESH_MS);
    const onVis = () => { if (!cancelled && !document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [apiUrl]);

  // Group subgroves by chain bucket for the donut chart.
  const slices = (() => {
    const counts: Record<string, number> = {};
    for (const s of kpis?.subgroves ?? []) {
      const c = chainOf(s);
      counts[c] = (counts[c] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value, color: chainColorMap[name] ?? chainColorFallback }))
      .sort((a, b) => b.value - a.value);
  })();

  // The subgroves behind the donut, most-indexed first — fills the panel's
  // lower half and gives a direct jump into each indexing job.
  const sortedSubgroves = [...(kpis?.subgroves ?? [])].sort(
    (a, b) => (b.indexer_count ?? 0) - (a.indexer_count ?? 0),
  );

  return (
    <div className="home">
      <header className="page-header">
        <h1>Home</h1>
        <p>
          {SHOW_SUBGROVES
            ? "Snapshot of the Willow network — what's indexed, who's running it, and how to use it."
            : 'Snapshot of the Willow network: the latest block, its validators, and its indexers.'}
        </p>
      </header>

      <div className={SHOW_SUBGROVES ? 'kpi-strip' : 'kpi-strip kpi-strip--three'}>
        <KpiTile
          label="latest block"
          value={latestBlock != null ? `#${latestBlock.toLocaleString()}` : '—'}
          loading={!consensusHasLoaded}
        />
        {SHOW_SUBGROVES && (
          <KpiTile
            label="subgroves"
            value={kpis?.subgroveCount?.toString() ?? '—'}
            loading={!hasLoaded}
          />
        )}
        <KpiTile
          label="indexers"
          value={kpis?.indexerCount?.toString() ?? '—'}
          loading={!hasLoaded}
        />
        <KpiTile
          label="validators"
          value={kpis?.validatorCount?.toString() ?? '—'}
          loading={!hasLoaded}
        />
      </div>

      {SHOW_SUBGROVES && (
      <div className="home-split">
        <section className="panel">
          <h2>Subgroves by chain</h2>
          <p className="panel-description">
            Live distribution of indexing jobs running on the network.
          </p>
          {!hasLoaded ? (
            <div className="donut-row">
              <div className="skeleton skeleton-donut" aria-label="Loading" />
              <ul className="donut-legend">
                {[0, 1, 2].map((i) => (
                  <li key={i}>
                    <span className="skeleton skeleton-line" />
                  </li>
                ))}
              </ul>
            </div>
          ) : slices.length > 0 ? (
            <>
              <div className="donut-row">
                <DonutChart slices={slices} />
                <ul className="donut-legend">
                  {slices.map((s) => (
                    <li key={s.name}>
                      <span className="donut-swatch" style={{ background: s.color }} />
                      <span className="donut-name">{s.name}</span>
                      <span className="donut-value">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <ul className="subgrove-mini-list">
                {sortedSubgroves.map((s) => {
                  const id = s.subgrove_id ?? s.id ?? '';
                  const n = s.indexer_count ?? 0;
                  const row = (
                    <>
                      <span
                        className="donut-swatch"
                        style={{ background: chainColorMap[chainOf(s)] ?? chainColorFallback }}
                      />
                      <span className="subgrove-mini-name">{id}</span>
                      <span className="subgrove-mini-meta">
                        {n} {n === 1 ? 'indexer' : 'indexers'}
                      </span>
                    </>
                  );
                  return (
                    <li key={id}>
                      {SHOW_SUBGROVES ? (
                        <button
                          type="button"
                          className="subgrove-mini-row"
                          onClick={() => navigate(`/subgroves/${encodeURIComponent(id)}`)}
                        >
                          {row}
                        </button>
                      ) : (
                        <div className="subgrove-mini-row subgrove-mini-row--static">
                          {row}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="muted">No subgroves indexed yet.</p>
          )}
        </section>

        <section className="panel">
          <div className="panel-eyebrow">Featured subgrove</div>
          <h2>Uniswap V3 ETH / USDC</h2>
          <p className="panel-description">
            Live ETH/USDC price derived from Uniswap V3's <code>sqrtPriceX96</code>{' '}
            on every <code>Swap</code> event Willow indexes. Updates every 30s.
          </p>
          {pricePoints === null ? (
            <div className="skeleton skeleton-chart" aria-label="Loading" />
          ) : pricePoints.length < 2 ? (
            <p className="muted">Waiting for the next swap to land — chart needs at least two points to draw.</p>
          ) : (
            <PriceLineChart points={pricePoints} />
          )}
          {pricePoints !== null && pricePoints.length >= 2 && priceVerification !== null && (
            <VerificationFootline result={priceVerification} />
          )}
        </section>
      </div>
      )}

      {/* Two-action CTA — the explorer's two primary jobs. Lives below
          the live status so the page leads with what's there before
          surfacing what to do. Both actions live in the Subgroves section,
          so the whole grid hides with it. */}
      {SHOW_SUBGROVES && (
      <div className="cta-grid">
        <div className="cta-card">
          <div className="cta-eyebrow">Query</div>
          <h3>Query indexed data</h3>
          <p>
            Run SQL, GraphQL, or instant charts against any indexed subgrove —
            every result is Willow verified. Free to read.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/subgroves')}>
            Browse subgroves →
          </button>
        </div>
        <div className="cta-card">
          <div className="cta-eyebrow">Build</div>
          <h3>Index a contract</h3>
          <p>
            Register any contract on any chain and Willow's network of indexers
            will keep it live with cryptographic proofs. Pay-as-you-go.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/subgroves/register')}>
            Register a Subgrove →
          </button>
        </div>
      </div>
      )}

      {/* The storage product is parked: what's here today is the RPC and the
          network behind it. */}
      {!SHOW_SUBGROVES && (
        <div className="cta-grid">
          <div className="cta-card">
            <div className="cta-eyebrow">RPC</div>
            <h3>Call all seven chains</h3>
            <p>One API key works on {CHAIN_NAMES}.</p>
            <Link className="btn btn-primary" to="/signup">
              Get an API key →
            </Link>
          </div>
          <div className="cta-card">
            <div className="cta-eyebrow">Network</div>
            <h3>See the network</h3>
            <p>The current state root, the validators producing blocks, and the indexers.</p>
            <Link className="btn btn-primary" to="/network">
              View the network →
            </Link>
          </div>
        </div>
      )}

    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  loading,
}: {
  label: string;
  value: string;
  sub?: string;
  loading?: boolean;
}) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label">{label}</div>
      {loading ? (
        <div className="skeleton skeleton-kpi" aria-label="Loading" />
      ) : (
        <>
          <div className="kpi-value">{value}</div>
          {sub && <div className="kpi-sub">{sub}</div>}
        </>
      )}
    </div>
  );
}

function VerificationFootline({ result }: { result: VerifyResult }) {
  if (result.kind === 'verified') {
    return (
      <p
        className="chart-verify chart-verify-ok"
        title={`${result.verifiedCount}/${result.totalProofs} Merkle proofs verified against the indexer's claimed state root.`}
      >
        ✓ Willow verified ({result.verifiedCount}/{result.totalProofs} proofs)
      </p>
    );
  }
  if (result.kind === 'failed') {
    return (
      <p className="chart-verify chart-verify-err" title={result.message}>
        ✗ Verification failed — {result.message ?? 'unknown error'}
      </p>
    );
  }
  return (
    <p className="chart-verify chart-verify-muted" title={result.message}>
      unverified — {result.message ?? 'proof not available'}
    </p>
  );
}

function PriceLineChart({ points }: { points: PricePoint[] }) {
  const W = 600;
  const H = 220;
  const pad = 50;
  const w = W - pad * 2;
  const h = H - pad * 2;

  const xs = points.map((p) => p.block);
  const ys = points.map((p) => p.price);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  // Pad y-axis range slightly so the line doesn't touch the edges.
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const ySpan = Math.max(1, yMax - yMin);
  const minY = yMin - ySpan * 0.1;
  const maxY = yMax + ySpan * 0.1;
  const rangeX = Math.max(1, maxX - minX);
  const rangeY = Math.max(1, maxY - minY);

  const scaled = points.map((p) => [
    pad + ((p.block - minX) / rangeX) * w,
    pad + h - ((p.price - minY) / rangeY) * h,
  ]);
  const linePath = scaled.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const fillPath = `${linePath} L${scaled[scaled.length - 1][0].toFixed(1)},${pad + h} L${scaled[0][0].toFixed(1)},${pad + h} Z`;

  const fmtUsd = (v: number) =>
    `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const latestPrice = points[points.length - 1].price;

  return (
    <div className="price-chart-root">
      <div className="price-headline">
        <span className="price-headline-value">{fmtUsd(latestPrice)}</span>
        <span className="price-headline-label">per ETH · latest swap</span>
      </div>
      <svg className="price-chart-svg" viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="price-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#price-fill)" />
        <path d={linePath} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" />
        <text x={pad - 6} y={pad - 4} fontSize={11} fill="var(--text-muted)" textAnchor="end">
          {fmtUsd(maxY)}
        </text>
        <text x={pad - 6} y={pad + h + 4} fontSize={11} fill="var(--text-muted)" textAnchor="end">
          {fmtUsd(minY)}
        </text>
        <text x={pad} y={H - 8} fontSize={11} fill="var(--text-muted)">
          block #{minX.toLocaleString()}
        </text>
        <text x={W - pad} y={H - 8} fontSize={11} fill="var(--text-muted)" textAnchor="end">
          block #{maxX.toLocaleString()}
        </text>
      </svg>
    </div>
  );
}

function DonutChart({ slices }: { slices: { name: string; value: number; color: string }[] }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) return null;

  const radius = 56;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * radius;
  let offsetSoFar = 0;

  return (
    <svg className="donut-svg" viewBox="0 0 160 160" width={160} height={160} aria-hidden="true">
      <circle
        cx={80}
        cy={80}
        r={radius}
        fill="transparent"
        stroke="var(--border)"
        strokeWidth={strokeWidth}
      />
      <g transform="rotate(-90 80 80)">
        {slices.map((slice, i) => {
          const fraction = slice.value / total;
          const dash = fraction * circumference;
          const segment = (
            <circle
              key={i}
              cx={80}
              cy={80}
              r={radius}
              fill="transparent"
              stroke={slice.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offsetSoFar}
              strokeLinecap="butt"
            />
          );
          offsetSoFar += dash;
          return segment;
        })}
      </g>
      <text
        x={80}
        y={78}
        textAnchor="middle"
        fontSize="22"
        fontWeight="700"
        fill="var(--text)"
        fontFamily="var(--font-sans)"
      >
        {total}
      </text>
      <text
        x={80}
        y={96}
        textAnchor="middle"
        fontSize="10"
        fill="var(--text-muted)"
        fontFamily="var(--font-sans)"
        letterSpacing="0.05em"
      >
        SUBGROVES
      </text>
    </svg>
  );
}
